import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PlanType, SHIELD_PATHS, ShieldAccessListsApiStore, ShieldApiStore } from '@bn/app-shield';
import { CDN_ROUTES } from '@bn/dashboard-core/constants';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { AbstractApiService } from '@bn/shared/entities/abstracts/abstract-api.service';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { SHIELD_LIMITS } from '../configs';

@Injectable({
  providedIn: 'root',
})
export class ShieldService extends AbstractApiService {
  protected readonly route = inject(ActivatedRoute);
  protected readonly dialog = inject(BsModalService);
  protected readonly router = inject(Router);
  readonly #shieldStore = inject(ShieldApiStore);
  readonly #shieldAccessListsStore = inject(ShieldAccessListsApiStore);

  customListCount = this.#shieldAccessListsStore.customListCount;
  customEntryCount = this.#shieldAccessListsStore.customEntryCount;

  changePlan(requestedPlan: PlanType): void {
    const currentShieldPlan = this.#shieldStore.selectedEntity()()?.planType ?? 0;
    const pullzoneId: number | null = this.#shieldStore.selectedEntity()()?.pullZoneId ?? null;
    const shieldId = this.#shieldStore.selectedEntityId()();
    const isUpgrading = currentShieldPlan < requestedPlan;
    const icon = (isUpgrading ? 'shield-check' : 'shield-slash') as IconProp;
    const downgradeOptions = this.isDowngradePossible(requestedPlan);
    const requestedPlanName = PlanType[requestedPlan];
    const currentPlanName = PlanType[currentShieldPlan];
    let confirmButtonName = `Yes, ${isUpgrading ? 'Upgrade' : 'Downgrade'}`;
    let description = `Are you sure you want to ${isUpgrading ? 'upgrade' : 'downgrade'} from ${currentPlanName} to ${requestedPlanName}? ${isUpgrading ? '' : 'This will disable Advanced Bunny Shield features.'}`;

    if (downgradeOptions.errorKey === 'waf-rules') {
      confirmButtonName = 'Review Rules';
      description = `You are currently exceeding ${requestedPlanName} plan limits due to active ${currentPlanName} features. Please delete your rules or disable features to meet ${requestedPlanName} plan requirements before downgrading.`;
    }
    if (downgradeOptions.errorKey === 'access-lists') {
      confirmButtonName = 'Review Access Lists';
      description = `You are currently exceeding ${requestedPlanName} plan limits due to active ${currentPlanName} features. Please delete your custom access lists or disable features to meet ${requestedPlanName} plan requirements before downgrading.`;
    }

    if (downgradeOptions.errorKey === 'rate-limits') {
      confirmButtonName = 'Review Rate Limits';
      description = `You are currently exceeding ${requestedPlanName} plan limits due to active ${currentPlanName} features. Please delete your rate limits or disable features to meet ${requestedPlanName} plan requirements before downgrading.`;
    }

    const confirmDialogRef = this.dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: isUpgrading ? 'success' : 'danger',
          icon,
          confirmButtonName,
          description,
          event: {
            action: 'confirm',
          },
          title: `${isUpgrading ? 'Upgrade' : 'Downgrade'} to ${PlanType[requestedPlan]} plan`,
          textCenter: true,
        },
      },
    });

    this.subscriptions.add(
      confirmDialogRef.content?.getOnAction$().subscribe((event) => {
        if (event.action === 'cancel') return this.dialog.hide();

        if (!shieldId) return;

        if (downgradeOptions.errorKey === 'waf-rules') {
          this.router.navigate([AppRoutes.CDN, pullzoneId, CDN_ROUTES.Shield, SHIELD_PATHS.ShieldWaf, SHIELD_PATHS.ShieldWafRules], { relativeTo: this.route });

          return;
        }

        if (downgradeOptions.errorKey === 'access-lists') {
          this.router.navigate([AppRoutes.CDN, pullzoneId, CDN_ROUTES.Shield, SHIELD_PATHS.ShieldAccessLists, SHIELD_PATHS.ShieldAccessListsCustom], {
            relativeTo: this.route,
          });

          return;
        }

        if (downgradeOptions.errorKey === 'rate-limits') {
          this.router.navigate([AppRoutes.CDN, pullzoneId, CDN_ROUTES.Shield, SHIELD_PATHS.ShieldRateLimit, SHIELD_PATHS], {
            relativeTo: this.route,
          });

          return;
        }

        this.#shieldStore.updateShield({
          id: +shieldId,
          data: { planType: requestedPlan },
          customFields: [isUpgrading ? 'upgrade' : 'downgrade'],
          skipCleanRequest: false,
          toastr: {
            title: `Bunny Shield ${PlanType[requestedPlan]}`,
            message: `Bunny Shield ${PlanType[requestedPlan]} was successfully enabled`,
          },
        });
      }),
    );
  }

  isDowngradePossible(requestedPlanType: PlanType): { success: boolean; errorKey?: 'access-lists' | 'waf-rules' | 'rate-limits' } {
    const rateLimitRulesCount = this.#shieldStore.rateLimitsPagination()?.totalCount ?? 0;
    const customWafRulesCount = this.#shieldStore.wafCustomRules().length;
    const customListCount = this.customListCount?.() ?? 0;
    const customEntryCount = this.customEntryCount?.() ?? 0;

    if (customWafRulesCount > SHIELD_LIMITS[requestedPlanType].customWafRulesCount) {
      return {
        success: false,
        errorKey: 'waf-rules',
      };
    }

    if (customListCount > SHIELD_LIMITS[requestedPlanType].customListCount || customEntryCount > SHIELD_LIMITS[requestedPlanType].customEntryCount) {
      return {
        success: false,
        errorKey: 'access-lists',
      };
    }

    if (rateLimitRulesCount > SHIELD_LIMITS[requestedPlanType].rateLimitRulesCount) {
      return {
        success: false,
        errorKey: 'rate-limits',
      };
    }

    if (requestedPlanType === PlanType.Basic) {
      const rateLimitRules = this.#shieldStore.rateLimits();
      const wafEnums = this.#shieldStore.wafEnums();
      const hasPremiumRateLimitOptions = rateLimitRules.some((rule) => {
        const config = rule.ruleConfiguration;
        if (!config) return false;

        const actionTypeEnum = wafEnums.find((e) => e.enumName === 'RatelimitRuleActionType');
        if (config.actionType !== undefined && actionTypeEnum?.enumValues?.some((v) => v.value === config.actionType && v.isPremium)) {
          return true;
        }

        const operatorTypeEnum = wafEnums.find((e) => e.enumName === 'WafRuleOperatorType');
        if (config.operatorType !== undefined && operatorTypeEnum?.enumValues?.some((v) => v.value === config.operatorType && v.isPremium)) {
          return true;
        }

        const transformationTypeEnum = wafEnums.find((e) => e.enumName === 'WafRuleTransformationType');
        if (config.transformationTypes?.some((t) => transformationTypeEnum?.enumValues?.some((v) => v.value === t && v.isPremium))) {
          return true;
        }

        const variableTypeEnum = wafEnums.find((e) => e.enumName === 'WafRuleVariableType');
        if (config.variableTypes && variableTypeEnum?.enumValues) {
          const usedVariableTypeNames = Object.keys(config.variableTypes);
          const hasPremiumVariable = usedVariableTypeNames.some((name) => variableTypeEnum.enumValues?.some((v) => v.name === name && v.isPremium));
          if (hasPremiumVariable) {
            return true;
          }
        }

        const counterKeyTypeEnum = wafEnums.find((e) => e.enumName === 'WafRatelimitCounterTypeEnum');
        if (config.counterKeyType !== undefined && counterKeyTypeEnum?.enumValues?.some((v) => v.value === config.counterKeyType && v.isPremium)) {
          return true;
        }

        const timeframeEnum = wafEnums.find((e) => e.enumName === 'WafRateLimitTimeframeType');
        if (config.timeframe !== undefined && timeframeEnum?.enumValues?.some((v) => v.value === config.timeframe && v.isPremium)) {
          return true;
        }

        const blockTypeEnum = wafEnums.find((e) => e.enumName === 'WafRatelimitBlockType');
        return !!(config.blockTime !== undefined && blockTypeEnum?.enumValues?.some((v) => v.value === config.blockTime && v.isPremium));
      });

      if (hasPremiumRateLimitOptions) {
        return {
          success: false,
          errorKey: 'rate-limits',
        };
      }
    }

    return {
      success: true,
    };
  }

  getNextPlanByRateLimit(rateLimitRules: number): PlanType | null {
    const plansWithLimits = Object.entries(SHIELD_LIMITS).map(([plan, limits]) => ({
      plan: plan as unknown as PlanType,
      rateLimit: limits.rateLimitRulesCount,
    }));

    plansWithLimits.sort((a, b) => a.rateLimit - b.rateLimit);

    const currentIndex = plansWithLimits.findIndex((p) => p.rateLimit === rateLimitRules);

    if (currentIndex === -1) {
      return null;
    }

    return Number(plansWithLimits[currentIndex + 1]?.plan) || null;
  }
}
